import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import useQuotations from '../hooks/useQuotations';
import QuotationsList from './QuotationsList';
import QuotationDisplay from './QuotationDisplay';
import AdminHeader from './AdminHeader';
import './QuotationsManagement.css';

const QuotationsManagement = ({ onBack }) => {
  const { token } = useAuth();
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showQuotationModal, setShowQuotationModal] = useState(false);

  const {
    quotations,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    filters,
    updateFilters,
    clearFilters,
    handleSort,
    getSortIndicator,
    fetchQuotations,
    formatDate,
    formatCurrency,
    getQuotationStatusColor,
    calculateQuotationTotal,
    getCacheInfo
  } = useQuotations(token);

  // Handle viewing a quotation
  const handleViewQuotation = (quotation) => {
    // Use the quotation data that's already available in localStorage
    setSelectedQuotation(quotation);
    setShowQuotationModal(true);
  };

  // Handle closing the quotation modal
  const handleCloseQuotation = () => {
    setShowQuotationModal(false);
    setSelectedQuotation(null);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchQuotations();
  };

  // Get cache information for display
  const cacheInfo = getCacheInfo();


  return (
    <div className="admin-dashboard">
      <AdminHeader 
        title="Gestão de Cotações" 
        onBack={onBack}
        showBackButton={true}
      />

      <main className="dashboard-main">
        <div className="breadcrumb">
          <span className="breadcrumb-item" onClick={onBack}>Painel Administrativo</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item active">Gestão de Cotações</span>
        </div>

        <div className="dashboard-content">
          <div className="management-header">
            <div className="header-info">
              <h2>Cotações do Sistema</h2>
              <p>Visualize e gerencie todas as cotações do sistema</p>
              {cacheInfo ? (
                <div className="cache-info">
                  <small>
                    {cacheInfo.isStale ? (
                      <span style={{ color: '#dc3545' }}>
                        ⚠️ Dados do cache podem estar desatualizados (última atualização há {cacheInfo.minutesAgo} min)
                      </span>
                    ) : (
                      <span style={{ color: '#28a745' }}>
                        ✅ Dados carregados do cache (última atualização há {cacheInfo.minutesAgo} min)
                      </span>
                    )}
                  </small>
                </div>
              ) : quotations.length > 0 ? (
                <div className="cache-info">
                  <small style={{ color: '#6c757d' }}>
                    📡 Dados carregados do servidor
                  </small>
                </div>
              ) : null}
            </div>
            
            <div className="header-actions">
              <button 
                onClick={handleRefresh}
                className="refresh-btn"
                disabled={loading}
                title="Atualizar cotações do servidor"
              >
                🔄 {loading ? 'Atualizando...' : 'Atualizar'}
              </button>
            </div>
            
            <div className="header-stats">
              <div className="stat-card">
                <div className="stat-number">{quotations.length}</div>
                <div className="stat-label">Total de Cotações</div>
              </div>
            </div>
          </div>

          <div className="quotations-content">
            <QuotationsList
              quotations={quotations}
              loading={loading}
              error={error}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filters={filters}
              updateFilters={updateFilters}
              clearFilters={clearFilters}
              handleSort={handleSort}
              getSortIndicator={getSortIndicator}
              formatDate={formatDate}
              formatCurrency={formatCurrency}
              getQuotationStatusColor={getQuotationStatusColor}
              calculateQuotationTotal={calculateQuotationTotal}
              onViewQuotation={handleViewQuotation}
            />
          </div>
        </div>
      </main>

      {/* Quotation Display Modal */}
      {showQuotationModal && (
        <QuotationDisplay
          quotation={selectedQuotation}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          getQuotationStatusColor={getQuotationStatusColor}
          calculateQuotationTotal={calculateQuotationTotal}
          onClose={handleCloseQuotation}
        />
      )}
    </div>
  );
};

export default QuotationsManagement;
